
import { useState, useEffect } from 'react';
import { gamesService, Game } from '@/services/gamesService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useInternalAuth } from './useInternalAuth';
import { toast } from 'sonner';

export interface VenueGameData {
  id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  venue_name?: string;
  home_team_name?: string;
  away_team_name?: string;
  home_team_id?: number;
  away_team_id?: number;
  game_code: string;
  status: string;
  game_status?: string;
}

export const useGames = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [upcomingGames, setUpcomingGames] = useState<Game[]>([]);
  const [userGames, setUserGames] = useState<Game[]>([]);
  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [venueGames, setVenueGames] = useState<VenueGameData[]>([]);
  const [loading, setLoading] = useState(false);

  const user = supabaseUser || internalUser;
  const userRole = supabaseUser ? profile?.role : internalUser?.role;

  const loadUpcomingGames = async () => {
    setLoading(true);
    try {
      const games = await gamesService.getUpcomingGames();
      setUpcomingGames(games);
    } catch (error) {
      console.error('Error loading upcoming games:', error);
      toast.error('Failed to load upcoming games');
    } finally {
      setLoading(false);
    }
  };

  const loadUserGames = async () => {
    if (!user?.id) return;
    
    try {
      const games = await gamesService.getGamesForUserTeams(user.id);
      setUserGames(games);
    } catch (error) {
      console.error('Error loading user games:', error);
      toast.error('Failed to load your team games');
    }
  };

  const loadVenueGames = async () => {
    if (!user?.id || (userRole !== 'coach' && userRole !== 'team-owner')) {
      console.log('🎮 VENUE GAMES: User not authorized or missing user data');
      return;
    }
    
    console.log('🎮 VENUE GAMES: Loading for user:', user.id, 'role:', userRole);
    setLoading(true);
    
    try {
      // First, get user's team IDs based on role
      let userTeamIds: number[] = [];
      
      if (userRole === 'coach') {
        const { data: coachTeams } = await supabase
          .from('team_coaches')
          .select('team_id')
          .eq('coach_id', user.id);
        userTeamIds = coachTeams?.map(t => t.team_id) || [];
      } else if (userRole === 'team-owner') {
        const { data: ownedTeams } = await supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', user.id);
        userTeamIds = ownedTeams?.map(t => t.team_id) || [];
      }

      console.log('🎮 VENUE GAMES: User team IDs:', userTeamIds);

      if (userTeamIds.length === 0) {
        console.log('🎮 VENUE GAMES: No teams found for user');
        setVenueGames([]);
        return;
      }

      // Load venue games data - games where user's teams are involved OR games that are open for registration
      const { data: allGames, error: gamesError } = await supabase
        .from('venue_games')
        .select('*')
        .in('status', ['approved', 'pending', 'confirmed'])
        .order('game_date', { ascending: true });

      if (gamesError) {
        console.error('🎮 VENUE GAMES: Error fetching games:', gamesError);
        throw new Error(`Failed to fetch games: ${gamesError.message}`);
      }

      // Filter games where current user's teams are involved OR games with open spots
      const userGames = allGames?.filter(game => {
        // Include if user's team is already assigned
        const userTeamInvolved = userTeamIds.includes(game.home_team_id) || 
          (game.away_team_id && userTeamIds.includes(game.away_team_id));
        
        // Include games with open spots (TBD teams) that user could potentially join
        const hasOpenSpots = !game.home_team_id || !game.away_team_id;
        
        return userTeamInvolved || hasOpenSpots;
      }) || [];

      console.log('🎮 VENUE GAMES: Filtered user games:', userGames.length);

      // Get team names for user's games
      const teamIds = [...new Set([
        ...userGames.map(g => g.home_team_id).filter(Boolean),
        ...userGames.map(g => g.away_team_id).filter(Boolean)
      ])];

      const { data: teams } = await supabase
        .from('teams')
        .select('team_id, team_name')
        .in('team_id', teamIds);

      // Get venue names
      const venueIds = [...new Set(userGames.map(g => g.venue_id).filter(Boolean))];
      const { data: venues } = await supabase
        .from('venues')
        .select('venue_id, venue_name')
        .in('venue_id', venueIds);

      // Convert to VenueGameData format
      const venueGameData = userGames.map(game => {
        const venue = venues?.find(v => v.venue_id === game.venue_id);
        const homeTeam = teams?.find(t => t.team_id === game.home_team_id);
        const awayTeam = teams?.find(t => t.team_id === game.away_team_id);

        return {
          id: game.id,
          game_title: game.game_title || `${homeTeam?.team_name || 'Home Team'} vs ${awayTeam?.team_name || 'Away Team'}`,
          game_date: game.game_date,
          game_time: game.game_time,
          venue_name: venue?.venue_name || 'TBD',
          home_team_name: homeTeam?.team_name || 'TBD',
          away_team_name: awayTeam?.team_name || 'TBD',
          home_team_id: game.home_team_id,
          away_team_id: game.away_team_id,
          game_code: game.game_code,
          status: game.status,
          game_status: game.game_status
        };
      });
      
      console.log('🎮 VENUE GAMES: Final venue game data:', venueGameData.length);
      setVenueGames(venueGameData);
    } catch (error) {
      console.error('🎮 VENUE GAMES: Error loading venue games:', error);
      toast.error(`Failed to load game data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentGames = async () => {
    try {
      const games = await gamesService.getRecentGames();
      setRecentGames(games);
    } catch (error) {
      console.error('Error loading recent games:', error);
      toast.error('Failed to load recent games');
    }
  };

  useEffect(() => {
    loadUpcomingGames();
    loadRecentGames();
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadUserGames();
      loadVenueGames();
    }
  }, [user?.id, userRole]);

  return {
    upcomingGames,
    userGames,
    recentGames,
    venueGames,
    loading,
    loadUpcomingGames,
    loadUserGames,
    loadRecentGames,
    loadVenueGames
  };
};
